import { getApiClient } from './client'

import type {
  Conversation,
  ConversationFilters,
  ConversationStatus,
} from '@/types/conversation'

export interface AssignAgentPayload {
  agentId: string
}

export interface UpdateStatusPayload {
  status: ConversationStatus
  resolutionNote?: string
}

interface CustomerSubmission {
  id: string
  customer_name?: string
  customer_email?: string
  customer_phone?: string
  issue_description?: string
  status: string
  created_at: number
}

interface BackendSession {
  id: string
  widget_id: string
  status: ConversationStatus
  created_at: number
  updated_at?: number
  closed_at?: number
  customer_name?: string
  customer_email?: string
  customer_phone?: string
  agent_user_id?: string
  agent_name?: string
  last_message_preview?: string
  subject?: string
  submission?: CustomerSubmission | null
}

/**
 * Parse message content that may be stored as JSON (legacy structured messages).
 * Some system messages (e.g. agent_unavailable_options) were historically saved
 * as JSON.stringify({text, metadata}) — this extracts the .text for display.
 */
function parseMessageContent(content: string): string {
  if (content.startsWith('{') && content.includes('"text"')) {
    try {
      const parsed = JSON.parse(content);
      if (typeof parsed.text === 'string') return parsed.text;
    } catch { /* not valid JSON, use as-is */ }
  }
  return content;
}

const transformSession = (session: BackendSession): Conversation => ({
  id: session.id,
  widgetId: session.widget_id,
  status: session.status,
  startedAt: new Date(session.created_at).toISOString(),
  updatedAt: new Date(session.updated_at || session.created_at).toISOString(),
  closedAt: session.closed_at ? new Date(session.closed_at).toISOString() : undefined,
  visitorName: session.customer_name || undefined,
  visitorEmail: session.customer_email || undefined,
  visitorPhone: session.customer_phone || session.submission?.customer_phone || undefined,
  assignedAgentId: session.agent_user_id || undefined,
  assignedAgentName: session.agent_name || undefined,
  channel: 'web',
  lastMessagePreview: session.last_message_preview ? parseMessageContent(session.last_message_preview) : undefined,
  subject: session.subject || `Conversation with ${session.customer_name || 'Anonymous'}`,
  submission: session.submission ? {
    id: session.submission.id,
    customerName: session.submission.customer_name,
    customerEmail: session.submission.customer_email,
    customerPhone: session.submission.customer_phone,
    issueDescription: session.submission.issue_description,
    status: session.submission.status,
    submittedAt: new Date(session.submission.created_at).toISOString(),
  } : undefined,
})

export const conversationsApi = {
  list: async (filters: ConversationFilters) => {
    const client = getApiClient()

    // Map dashboard filters to API parameters
    const params: Record<string, string | number> = {
      limit: filters.pageSize || 25,
      offset: ((filters.page || 1) - 1) * (filters.pageSize || 25),
    }

    // Map status filter
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status
    } else {
      params.status = 'all'
    }

    // Add search parameter
    if (filters.search) {
      params.search = filters.search
    }

    const response = await client.get<{
      sessions: BackendSession[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>('/chat/sessions', { params })

    // Transform API response to match dashboard expectations
    return {
      data: response.data.sessions.map(transformSession),
      total: response.data.pagination.total,
      page: filters.page || 1,
      pageSize: filters.pageSize || 25,
    }
  },
  myChats: async (filters: ConversationFilters) => {
    const client = getApiClient()

    // Map dashboard filters to API parameters
    const params: Record<string, string | number> = {
      limit: filters.pageSize || 25,
      offset: ((filters.page || 1) - 1) * (filters.pageSize || 25),
    }

    // Map status filter
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status
    } else {
      params.status = 'all'
    }

    const response = await client.get<{
      sessions: BackendSession[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>('/chat/my-sessions', { params })

    // Transform API response to match dashboard expectations
    return {
      data: response.data.sessions.map(transformSession),
      total: response.data.pagination.total,
      page: filters.page || 1,
      pageSize: filters.pageSize || 25,
    }
  },
  get: async (conversationId: string) => {
    const client = getApiClient()
    const response = await client.get<{ session: BackendSession }>(`/chat/sessions/${conversationId}`)
    return transformSession(response.data.session)
  },
  getMessages: async (conversationId: string) => {
    const client = getApiClient()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await client.get<{ messages: any[] }>(
      `/chat/sessions/${conversationId}/messages`,
    )
    // Transform messages to match dashboard format
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return response.data.messages.map((msg: any) => ({
      id: msg.id,
      conversationId: msg.session_id,
      senderId: msg.sender_id,
      senderType: msg.sender_type,
      body: msg.content,
      createdAt: new Date(msg.created_at).toISOString(),
      imageId: msg.image_id || undefined,
    }))
  },
  assignAgent: async (conversationId: string, payload: AssignAgentPayload) => {
    const client = getApiClient()
    await client.post(`/chat/sessions/${conversationId}/assign`, { agent_id: payload.agentId })
  },
  updateStatus: async (conversationId: string, payload: UpdateStatusPayload) => {
    const client = getApiClient()
    if (payload.status === 'resolved') {
      await client.post(`/chat/sessions/${conversationId}/resolve`)
    } else if (payload.status === 'closed') {
      await client.post(`/chat/sessions/${conversationId}/close`)
    }
  },
  acceptNotification: async (sessionId: string) => {
    const client = getApiClient()
    const response = await client.post<{
      success: boolean
      message: string
      customerName?: string
      customerEmail?: string
    }>(`/chat/notifications/${sessionId}/accept`)
    return response.data
  },
  transferToAI: async (conversationId: string) => {
    const client = getApiClient()
    const response = await client.post<{ message: string; sessionId: string }>(
      `/chat/sessions/${conversationId}/transfer-to-ai`,
    )
    return response.data
  },
}
